import type { Dictionary } from '@/i18n/dictionary'
import { mediusTermsEn } from '@/i18n/medius-terms'
import { mediusDeletionEn, mediusPrivacyEn } from '@/i18n/medius-policies'
import { sparsaAcceptableUseEn, sparsaTermsEn } from '@/i18n/sparsa-terms'
import { sparsaDeletionEn, sparsaPrivacyEn } from '@/i18n/sparsa-policies'

export const en: Dictionary = {
  meta: {
    home: {
      title: 'Dalmos Systems — Custom Business Software, CRM and Integrations',
      description:
        'Dalmos Systems builds custom business software for finance, real estate, hospitality and logistics — CRM, ERP and PMS integrations, cloud infrastructure and long-term maintenance.',
      keywords: [
        'custom business software',
        'CRM implementation',
        'real estate software',
        'hospitality software',
        'ERP and PMS integrations',
        'API integrations',
        'Microsoft Dynamics 365',
        'Power Platform',
        'software development Croatia',
      ],
    },
    privacy: {
      title: 'Privacy Policy — Dalmos Systems',
      description:
        'How Dalmos Systems processes personal data submitted through the contact form, in line with the GDPR.',
    },
    legal: {
      title: 'Legal notice — Dalmos Systems',
      description:
        'Legal details of Dalmos Systems: registered name, seat in Šibenik, Crafts Register entry and contact details.',
    },
    mediusPrivacy: {
      title: 'Medius privacy policy — Dalmos Systems',
      description:
        'How Medius by Dalmos Systems processes personal data from e-mail, chat, WhatsApp, Messenger, Instagram and calls on behalf of its customers.',
    },
    mediusDeletion: {
      title: 'Medius data deletion — Dalmos Systems',
      description:
        'How to request deletion of your personal data from Medius, including messages received through Facebook Messenger, Instagram and WhatsApp.',
    },
    mediusTerms: {
      title: 'Medius terms of service — Dalmos Systems',
      description:
        'The terms under which Dalmos Systems provides Medius, its communication workspace for e-mail, chat, messaging and calls, to business customers.',
    },
    sparsaPrivacy: {
      title: 'Sparsa privacy notice — Dalmos Systems',
      description:
        'How Sparsa by Dalmos processes the personal data of newsletter recipients and app users, and measures opens and clicks only with consent.',
    },
    sparsaDeletion: {
      title: 'Sparsa data deletion — Dalmos Systems',
      description:
        'How to stop newsletters sent through Sparsa by Dalmos and how to ask for your personal data to be deleted.',
    },
    sparsaTerms: {
      title: 'Sparsa terms of service — Dalmos Systems',
      description:
        'The terms under which Dalmos Systems provides Sparsa by Dalmos, its newsletter service, to businesses and other organisations.',
    },
    sparsaAcceptableUse: {
      title: 'Sparsa acceptable use policy — Dalmos Systems',
      description:
        'The rules for sending with Sparsa by Dalmos: consent for every recipient, no bought or scraped lists, import attestations and how to report abuse.',
    },
    notFound: {
      title: 'Page Not Found — Dalmos Systems',
      description: 'We could not find the page you requested.',
    },
  },
  nav: {
    home: 'Home',
    services: 'Services',
    about: 'About',
    contact: 'Contact',
    cta: 'Request a quote',
    openMenu: 'Open navigation',
    closeMenu: 'Close navigation',
    languageLabel: 'Choose a language',
    currentLanguage: 'Current language',
  },
  a11y: {
    skipToContent: 'Skip to content',
    decorative: 'Decorative element',
  },
  hero: {
    eyebrow: 'Custom software solutions for business',
    headlineBefore: 'Systems that work for your business,',
    headlineAccent: 'not the other way around.',
    subtitle:
      'A technical partner for custom business software — solutions for finance, real estate, hospitality and logistics, built on experience from large CRM projects, with the Microsoft ecosystem as a reliable foundation rather than the only focus.',
    ctaPrimary: 'Request a quote',
    ctaSecondary: 'See the services',
    chips: [
      'CRM and ERP integrations',
      'Hospitality and PMS',
      'Financial solutions',
      'Real estate',
      'Mobile apps',
      'Warehouse and facility',
    ],
    scrollHint: 'Scroll',
  },
  stats: {
    title: 'What we build on',
    items: [
      { value: '20+', label: 'Service areas under one roof' },
      { value: '6', label: 'Industries with proven solution patterns' },
      { value: '1', label: 'Person who runs the project and writes the code' },
      { value: '100%', label: 'Of projects ship with defined support' },
    ],
  },
  why: {
    title: 'Why Dalmos Systems',
    cards: [
      {
        title: 'Direct access',
        body: 'No agency layers — you speak directly to the person who builds the project.',
      },
      {
        title: 'Industry-shaped solutions',
        body: 'From finance and real estate to hospitality and logistics — every system is built around your actual processes.',
      },
      {
        title: 'A partner, not a contractor',
        body: 'We stay with the system after delivery — maintenance, upgrades and support are part of the deal.',
      },
    ],
  },
  services: {
    title: 'A broad range of solutions, one source',
    lede: 'From finance and real estate to hospitality and cloud infrastructure — every service comes with implementation and long-term maintenance, without over-focusing on one narrow niche.',
    items: {
      crm: {
        title: 'CRM and customisation',
        body: 'Rolling out and tailoring CRM systems to your processes, teams and clients.',
      },
      erp: {
        title: 'ERP systems and integrations',
        body: 'Connecting ERP with sales, warehouse and finance, and adapting existing modules.',
      },
      processes: {
        title: 'Process automation and optimisation',
        body: 'Automating internal workflows, with applications that speed up decisions and cut manual work.',
      },
      finance: {
        title: 'Financial solutions',
        body: 'Systems for cost tracking, billing and financial reporting.',
      },
      realEstate: {
        title: 'Real estate and asset management',
        body: 'Managing property portfolios, lease agreements and valuations.',
      },
      mobile: {
        title: 'Mobile applications',
        body: 'Mobile extensions of business systems for field, warehouse or sales teams.',
      },
      hospitality: {
        title: 'Hospitality solutions',
        body: 'Systems for hotels, restaurants and accommodation providers — PMS integrations, bookings, guest data and operations.',
      },
      web: {
        title: 'Web solutions and portals',
        body: 'Websites aligned with your business system.',
      },
      reservations: {
        title: 'Reservation systems',
        body: 'Real-time online bookings, connected to your PMS and billing.',
      },
      analytics: {
        title: 'Statistics and reporting',
        body: 'Dashboards for decisions grounded in real data.',
      },
      loyalty: {
        title: 'Loyalty systems',
        body: 'Loyalty programmes with points, tiers and rewards, member cards and a mobile app or wallet pass. Offers and campaigns follow each member’s purchase history, and everything stays connected to your CRM.',
      },
      marketing: {
        title: 'Marketing and newsletters',
        body: 'Newsletters and campaigns to your CRM audience, with consent and unsubscribes built in.',
      },
      communication: {
        title: 'Communication solutions',
        body: 'Custom solutions for how your team talks to customers, built around your CRM or standalone.',
      },
      warehouse: {
        title: 'Warehouse management',
        body: 'Tracking stock levels and goods movement.',
      },
      facility: {
        title: 'Facility management',
        body: 'Managing facilities and maintenance.',
      },
      api: {
        title: 'API integrations and middleware',
        body: 'Connecting CRM, ERP, PMS and other systems that do not talk to each other.',
      },
      cloud: {
        title: 'Cloud infrastructure and DevOps',
        body: 'Azure environments, CI/CD and automation.',
      },
      microsoft: {
        title: 'The Microsoft ecosystem',
        body: 'Dynamics 365, Power Platform and Azure, when they are the right option for the business.',
      },
      security: {
        title: 'IT security and compliance',
        body: 'Data protection and GDPR compliance.',
      },
      custom: {
        title: 'Custom software solutions',
        body: 'Bespoke solutions for specific needs.',
      },
      training: {
        title: 'Training and onboarding',
        body: 'User training and internal documentation.',
      },
      support: {
        title: 'Support and SLA agreements',
        body: 'Defined response and support windows.',
      },
    },
  },
  integrations: {
    title: 'Systems that finally talk to each other',
    lede: 'CRM, ERP and PMS rarely connect on their own. We link them so data is entered once and seen everywhere — no retyping, no spreadsheets in between.',
    hubLabel: 'One source of truth',
    systems: [
      {
        code: 'CRM',
        title: 'Customers and sales',
        body: 'Clients, opportunities and communication in one place, in sync with quotes, contracts and billing.',
      },
      {
        code: 'ERP',
        title: 'Finance and operations',
        body: 'Invoicing, purchasing and stock connected to sales and the warehouse, with no double entry.',
      },
      {
        code: 'PMS',
        title: 'Hospitality',
        body: 'Property management systems connected to bookings, CRM and accounting.',
      },
      {
        code: 'API',
        title: 'Everything else',
        body: 'Accounting, e-commerce, POS, channel managers, BI tools and internal apps.',
      },
    ],
    capabilitiesTitle: 'What you get',
    capabilities: [
      'Two-way, real-time data sync',
      'Data migrations without downtime',
      'Custom middleware and APIs',
      'Monitoring, logs and alerts when something breaks',
    ],
  },
  about: {
    title: 'About us',
    statementBefore: 'A new company.',
    statementAccent: 'Experience that does not start from zero.',
    lede: 'Dalmos Systems was founded as a technical partner for custom business software — finance, real estate, hospitality, logistics and more. The approach is a direct continuation of work on large CRM migration and integration projects in the real estate and hospitality sectors, without a large organisation sitting in between.',
    processTitle: 'How we work',
    steps: [
      {
        index: '01',
        title: 'Discovery',
        body: 'Understanding the process before the first line of code.',
      },
      {
        index: '02',
        title: 'Solution design',
        body: 'Architecture adapted to your systems, not the other way around.',
      },
      {
        index: '03',
        title: 'Implementation',
        body: 'Iterative delivery with regular check-ins with you.',
      },
      { index: '04', title: 'Support', body: 'We stay with the system after launch.' },
    ],
    valuesTitle: 'What guides us',
    values: [
      {
        title: 'Direct access',
        body: 'The person who runs the project is the person you talk to, with no layers of communication.',
      },
      {
        title: 'Transparency',
        body: 'Clear scope, clear price and clear project boundaries before the first line of code.',
      },
      {
        title: 'Built to last',
        body: 'We build systems we will maintain ourselves, not projects we leave behind.',
      },
    ],
  },
  contact: {
    title: 'Let us talk about your system',
    lede: 'Get in touch with a description of the project — we reply with a proposal for the next step.',
    responseNote: 'We reply within {days} business days.',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    form: {
      name: 'Full name',
      namePlaceholder: 'Jane Doe',
      email: 'Email',
      emailPlaceholder: 'jane@company.com',
      company: 'Company',
      companyPlaceholder: 'Company name (optional)',
      message: 'Message',
      messagePlaceholder: 'Describe the project, the systems you use and your target timeline.',
      consent: 'I agree that my data may be used to answer this enquiry.',
      submit: 'Send enquiry',
      submitting: 'Sending…',
      successTitle: 'Your enquiry was sent.',
      successBody: 'We will get back to you at the email address you provided.',
      errorTitle: 'Sending failed.',
      errorBody: 'Please try again or write to us directly by email.',
      fallbackHint: 'Open your email client',
      errors: {
        nameRequired: 'Please enter your full name.',
        emailRequired: 'Please enter your email address.',
        emailInvalid: 'That email address is not valid.',
        messageRequired: 'Please enter a message.',
        messageTooShort: 'The message must be at least 20 characters long.',
        consentRequired: 'Consent to data processing is required.',
      },
    },
  },
  footer: {
    tagline: 'Custom business software for finance, real estate, hospitality and logistics.',
    navTitle: 'Navigation',
    legalTitle: 'Legal',
    privacy: 'Privacy policy',
    legalNotice: 'Legal notice',
    rights: 'All rights reserved.',
    builtIn: 'Ulica Sedmog kontinenta 1, 22000 Šibenik, Croatia',
  },
  privacy: {
    title: 'Privacy policy',
    lastUpdatedLabel: 'Last updated',
    lastUpdated: '2026-10-31',
    intro:
      'This policy describes how Dalmos Systems processes the personal data you send us through this website.',
    sections: [
      {
        heading: 'Data controller',
        paragraphs: [
          'The data controller is {controller}. For any questions about data processing, write to {email}.',
        ],
      },
      {
        heading: 'What data we collect',
        paragraphs: [
          'Through the contact form we collect your name, email address, company name if you provide one, and the content of your message.',
          'We do not use advertising cookies or tools that track visitors across other websites.',
          'We count visits with Cloudflare Web Analytics, a cookieless script from Cloudflare (static.cloudflareinsights.com). When a page loads, it sends Cloudflare anonymous page-view and performance data: the address of the page, the page you came from, and load times and similar measurements of how the page performed; Cloudflare adds the browser, operating system, device type and country from the request. The script sets no cookies, uses no other storage on your device and creates no visitor identifier. Your IP address reaches Cloudflare with the request, as with any website; Cloudflare does not use it to identify you and does not store it for this. We see only totals, such as page views, referring sites, countries and device types, and use them to understand how the website is used and to keep it fast. Our legal basis is our legitimate interest in running and improving the website (Article 6(1)(f) GDPR). Cloudflare processes the data for us as our processor, under its data processing addendum with the EU standard contractual clauses for transfers outside the EU. It keeps the full data for 7 days and then a sample of about a tenth of it, from which we see statistics for the past six months. You can block the script with your browser’s settings or a content blocker; the website works the same without it. The same applies to the Medius website, medius.dalmossystems.com, which links to this policy.',
        ],
      },
      {
        heading: 'Purpose and legal basis',
        paragraphs: [
          'We use the data solely to answer your enquiry and prepare a quote. The legal basis is your consent together with our legitimate interest in communicating with prospective clients.',
        ],
      },
      {
        heading: 'Retention period',
        paragraphs: [
          'We keep enquiries for a maximum of twenty-four months from the last communication, after which they are deleted, unless longer retention is required by a contractual or legal obligation.',
        ],
      },
      {
        heading: 'Your rights',
        paragraphs: [
          'You have the right to access your data, to rectification, erasure, restriction of processing, data portability, and to withdraw your consent at any time.',
          'You may lodge a complaint with the Croatian Personal Data Protection Agency (AZOP) as the supervisory authority.',
        ],
      },
      {
        heading: 'Security and data transfer',
        paragraphs: [
          'Traffic between your browser and our servers is encrypted. We do not sell your data and do not share it with third parties other than service providers who help us operate the site and communicate with you, under a data processing agreement.',
        ],
      },
    ],
    backHome: 'Back to home',
  },
  legal: {
    title: 'Legal notice',
    intro:
      'Information about the service provider, as required by the Croatian Electronic Commerce Act.',
    nameLabel: 'Registered name',
    shortNameLabel: 'Short name',
    seatLabel: 'Registered seat',
    country: 'Croatia',
    registerLabel: 'Register',
    registerValue: 'Crafts Register of Croatia (Obrtni registar), Šibenik-Knin County',
    mboLabel: 'Registration number (MBO)',
    registeredOnLabel: 'Registered on',
    activityLabel: 'Main activity',
    activityValue:
      'Custom design, development and maintenance of computer programs and applications (NKD 2025: 62.10.9)',
    contactLabel: 'Contact',
    backHome: 'Back to home',
  },
  mediusPrivacy: mediusPrivacyEn,
  mediusDeletion: mediusDeletionEn,
  mediusTerms: mediusTermsEn,
  mediusLinks: {
    mediusPrivacy: 'Medius privacy policy',
    mediusDeletion: 'How to request deletion of your data',
    mediusTerms: 'Medius terms of service',
  },
  sparsaPrivacy: sparsaPrivacyEn,
  sparsaDeletion: sparsaDeletionEn,
  sparsaTerms: sparsaTermsEn,
  sparsaAcceptableUse: sparsaAcceptableUseEn,
  sparsaLinks: {
    sparsaPrivacy: 'Sparsa privacy notice',
    sparsaDeletion: 'How to stop newsletters and delete your data',
    sparsaTerms: 'Sparsa terms of service',
    sparsaAcceptableUse: 'Sparsa acceptable use policy',
  },
  notFound: {
    code: '404',
    title: 'Page not found',
    body: 'The address you opened does not exist or has been moved.',
    cta: 'Back to home',
  },
}
